import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Typography, Button, Paper, IconButton } from '@mui/material';
import { Formik, Form, FieldArray } from 'formik';
import { useMemo, useRef, useState } from 'react';

import { ResidencyStatusField } from './commonField';
import FormSkeleton from './formSkeleton';
import StepNote from './stepNote';
import { FormBusyProvider } from 'components/common/formikForm/formBusyContext';
import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import { StepNavigation } from 'components/v2/StepNavigation';

import { BooleanValue, DriverLicenses, genderList, maritalStatusList } from 'constant';
import useEnabledCountries from 'hooks/useEnabledCountries';
import {
    useCountries,
    useCitizenship,
    useRegions,
    useAdditionalDetails,
    useSaveAdditionalDetails,
    usePreviousVisaDetails,
    useSavePreviousVisaDetails,
} from 'hooks';
import useTranslate from 'hooks/useTranslate';
import type { AdditionalDetailsForm } from 'types';
import {
    formatDayjsDate,
    generateOptionFromObject,
    generateOptionsFromArray,
    generateOptionsFromString,
} from 'utils/dataFormatter';
import {
    omitHasSpouse,
    parseHasSpouseFromPayload,
    parseSpouseUserIdFromPayload,
    persistSpouseLifecycle,
} from 'utils/hasSpouse';
import { shouldShowResidencyProof } from 'utils/residencyProof';
import { isDestinationCountry } from 'utils/countries';
import {
    additionalDetailsDefaultValues,
    getAdditionalDetailValidationSchema,
} from 'utils/validations/additionalDetailsSchema';

const booleanOptions = generateOptionFromObject<boolean>(BooleanValue);

interface AdditionalDetailsStepProps {
    isLastStep: boolean;
    hasSpouse: string | null;
    formData: Record<string, unknown>;
    handleTravelingWithPartner: (value: string) => void;
    onNext: (data: Record<string, unknown>) => void;
    onBack: () => void;
}

const AdditionalDetailsStep = ({
    isLastStep,
    onNext,
    onBack,
    handleTravelingWithPartner,
}: AdditionalDetailsStepProps) => {
    const { translate } = useTranslate();
    const { hasCanada } = useEnabledCountries();
    const { data: apiData, isLoading } = useAdditionalDetails();
    const { data: citizenships, isLoading: citizenshipsLoading } = useCitizenship();
    const validationSchema = useMemo(
        () =>
            getAdditionalDetailValidationSchema(
                hasCanada,
                citizenships,
                translate('requiredField')
            ),
        [citizenships, hasCanada, translate]
    );
    const { data: countries, isLoading: countriesLoading } = useCountries();
    const { data: previousVisaData, isLoading: previousVisaLoading } = usePreviousVisaDetails();
    const { data: regions } = useRegions();
    const saveAdditionalDetailsMutation = useSaveAdditionalDetails();
    const savePreviousVisaMutation = useSavePreviousVisaDetails();
    const [unlinkSpouseOpen, setUnlinkSpouseOpen] = useState(false);
    const unlinkSpouseResolver = useRef<((confirmed: boolean) => void) | null>(null);

    const savedHasSpouse = parseHasSpouseFromPayload(apiData) === true;

    const translatedGenderOptions = useMemo(() => {
        return genderList.map((value) => translate(value));
    }, [translate]);
    const translatedMaritalStatusOptions = useMemo(() => {
        return maritalStatusList.map((value) => translate(value));
    }, [translate]);

    const genderOptions = generateOptionsFromArray(translatedGenderOptions);
    const maritalStatusOptions = generateOptionsFromArray(translatedMaritalStatusOptions);

    const initialValues: Partial<AdditionalDetailsForm> & { hasSpouse: boolean } = useMemo(() => {
        return {
            ...additionalDetailsDefaultValues,
            ...omitHasSpouse(apiData || {}),
            hasSpouse: parseHasSpouseFromPayload(apiData) === true,
            previousVisa: previousVisaData || [],
        };
    }, [apiData, previousVisaData]);

    if (
        isLoading ||
        countriesLoading ||
        previousVisaLoading ||
        (hasCanada && citizenshipsLoading)
    ) {
        return <FormSkeleton fields={14} />;
    }

    const confirmUnlinkSpouseIfNeeded = (travelingWithSpouse: boolean) => {
        if (!(savedHasSpouse && !travelingWithSpouse)) {
            return Promise.resolve(true);
        }
        return new Promise<boolean>((resolve) => {
            unlinkSpouseResolver.current = resolve;
            setUnlinkSpouseOpen(true);
        });
    };

    const handleUnlinkSpouseConfirm = () => {
        setUnlinkSpouseOpen(false);
        unlinkSpouseResolver.current?.(true);
        unlinkSpouseResolver.current = null;
    };

    const handleUnlinkSpouseCancel = () => {
        setUnlinkSpouseOpen(false);
        unlinkSpouseResolver.current?.(false);
        unlinkSpouseResolver.current = null;
    };

    const handleSubmit = async (
        values: Partial<AdditionalDetailsForm> & { hasSpouse?: boolean }
    ) => {
        const { previousVisa, hasSpouse: travelingWithSpouse, ...additionalDetails } = values;
        const hasSpouseValue = travelingWithSpouse === true;

        if (!(await confirmUnlinkSpouseIfNeeded(hasSpouseValue))) {
            return;
        }

        try {
            const response = await Promise.all([
                saveAdditionalDetailsMutation.mutateAsync({
                    ...omitHasSpouse(additionalDetails),
                    has_spouse: hasSpouseValue,
                } as AdditionalDetailsForm),
                savePreviousVisaMutation.mutateAsync(previousVisa || []),
            ]);
            persistSpouseLifecycle(hasSpouseValue, parseSpouseUserIdFromPayload(response[0]));
            handleTravelingWithPartner(hasSpouseValue ? 'Yes' : 'No');
            onNext({
                additionalDetails: response[0],
                previousVisa: response[1],
            });
        } catch {
            // Error alert is handled by the mutation hook onError
        }
    };

    const countryOptions = generateOptionsFromString(countries ?? []);
    const driverLicenseOptions = generateOptionsFromString(DriverLicenses);

    const isSubmittingForm =
        saveAdditionalDetailsMutation.isPending || savePreviousVisaMutation.isPending;

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isValid, isSubmitting, values, setFieldValue }) => {
                    const isLoading = isSubmitting || isSubmittingForm;
                    const countryOfResidence = (values.country_of_residence as string) || '';
                    const showRegion = isDestinationCountry(countryOfResidence);
                    const showResidencyProof = shouldShowResidencyProof(
                        hasCanada,
                        countryOfResidence,
                        citizenships
                    );
                    const regionOptions = generateOptionsFromString(
                        showRegion && countryOfResidence
                            ? (regions as Record<string, string[]> | null)?.[countryOfResidence] ||
                                  []
                            : []
                    );

                    return (
                        <FormBusyProvider busy={isLoading}>
                            <Form>
                                <FormGridLayout>
                                    <FormField
                                        type="radio"
                                        name="gender"
                                        label="gender"
                                        options={genderOptions}
                                        disabled={isLoading}
                                    />
                                    <FormField
                                        required
                                        type="radio"
                                        name="marital_status"
                                        label="maritalStatus"
                                        options={maritalStatusOptions}
                                        disabled={isLoading}
                                    />
                                    <FormField
                                        required
                                        maxDaysFromToday
                                        type="date"
                                        name="birth_date"
                                        label="dateOfBirth"
                                        disabled={isLoading}
                                    />
                                    <FormField
                                        required
                                        type="autosuggest"
                                        name="country_of_residence"
                                        label="countryOfResidence"
                                        options={countryOptions}
                                        disabled={isLoading}
                                        onChange={(value) => {
                                            setFieldValue('country_of_residence', value);
                                            // Clear region when country changes
                                            setFieldValue('region_of_residence', '');
                                        }}
                                    />
                                    {showRegion && (
                                        <FormField
                                            required
                                            type="autosuggest"
                                            name="region_of_residence"
                                            label="regionOfResidence"
                                            options={regionOptions}
                                            disabled={isLoading || !countryOfResidence}
                                        />
                                    )}
                                    <ResidencyStatusField
                                        required
                                        countryOfResidence={countryOfResidence}
                                        disabled={isLoading}
                                    />
                                    <FormField
                                        required
                                        type="monthyear"
                                        minDate={formatDayjsDate(values.birth_date || null)}
                                        name="residence_since"
                                        label="residenceStatusSince"
                                        disabled={isLoading}
                                    />
                                    <FormField
                                        type="autosuggest"
                                        name="driver_license"
                                        label="driversLicense"
                                        options={driverLicenseOptions}
                                        disabled={isLoading}
                                    />

                                    <StepNote messageKey="noteMinAssets" fullWidth />

                                    <FormField
                                        required
                                        name="income"
                                        label="approxYearlyIncome(USD)"
                                        type="number"
                                        disabled={isLoading}
                                    />
                                    <FormField
                                        required
                                        name="net_worth"
                                        label="netWorth(USD)"
                                        type="number"
                                        disabled={isLoading}
                                    />
                                    <FormField
                                        required
                                        name="liquid_assets"
                                        label="approxAvailableFunds(USD)"
                                        type="number"
                                        disabled={isLoading}
                                    />
                                    <FormField
                                        type="radio"
                                        name="criminal_record"
                                        label="criminalRecord"
                                        options={booleanOptions}
                                        disabled={isLoading}
                                    />
                                    {showResidencyProof && (
                                        <FormField
                                            required
                                            type="radio"
                                            name="has_residency_proof"
                                            label="hasResidencyProofQuestion"
                                            tooltip="hasResidencyProofTooltip"
                                            options={booleanOptions}
                                            disabled={isLoading}
                                        />
                                    )}
                                    <FormField
                                        type="radio"
                                        name="hasSpouse"
                                        label="travelWithSpouse"
                                        options={booleanOptions}
                                        disabled={isLoading}
                                        onChange={(value) => {
                                            setFieldValue('hasSpouse', value);
                                            const hasSpouseValue =
                                                value === true || value === 'true' ? 'Yes' : 'No';
                                            handleTravelingWithPartner(hasSpouseValue);
                                        }}
                                    />
                                    <FormField
                                        type="radio"
                                        name="class_2_with_airbrake"
                                        label="class2withAirbrakeQuestion"
                                        options={booleanOptions}
                                        disabled={isLoading}
                                    />

                                    {/* Past and Future Visas Section */}
                                    <Box
                                        sx={{
                                            gridColumn: '1 / -1',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mt: 3,
                                            mb: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                fontWeight: 600,
                                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                            }}
                                        >
                                            {translate('previouslyHeldVisas')}
                                        </Typography>
                                        <FieldArray name="previousVisa">
                                            {(arrayHelpers) => (
                                                <Button
                                                    startIcon={<AddIcon />}
                                                    onClick={() =>
                                                        arrayHelpers.push({
                                                            name: '',
                                                            start: '',
                                                            end: '',
                                                            country: '',
                                                        })
                                                    }
                                                    variant="outlined"
                                                    color="primary"
                                                >
                                                    Add Visa
                                                </Button>
                                            )}
                                        </FieldArray>
                                    </Box>

                                    <FieldArray name="previousVisa">
                                        {(arrayHelpers) => (
                                            <Box sx={{ gridColumn: '1 / -1' }}>
                                                {values.previousVisa &&
                                                values.previousVisa.length > 0 ? (
                                                    values.previousVisa.map((_, index) => (
                                                        <Paper
                                                            key={index}
                                                            sx={{
                                                                p: 3,
                                                                mb: 2,
                                                                bgcolor: 'grey.50',
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                borderRadius: 2,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    mb: 2,
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    sx={{ fontWeight: 600 }}
                                                                >
                                                                    Visa {index + 1}
                                                                </Typography>
                                                                <IconButton
                                                                    onClick={() =>
                                                                        arrayHelpers.remove(index)
                                                                    }
                                                                    size="small"
                                                                    color="error"
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </Box>

                                                            <FormGridLayout>
                                                                <FormField
                                                                    required
                                                                    name={`previousVisa.${index}.name`}
                                                                    label="visaName"
                                                                    placeholder="e.g., Work Permit, Study Visa"
                                                                    disabled={isLoading}
                                                                />
                                                                <FormField
                                                                    required
                                                                    type="autosuggest"
                                                                    name={`previousVisa.${index}.country`}
                                                                    label="country"
                                                                    options={countryOptions}
                                                                    disabled={isLoading}
                                                                />
                                                                <FormField
                                                                    required
                                                                    type="monthyear"
                                                                    name={`previousVisa.${index}.start`}
                                                                    label="startDate"
                                                                    disabled={isLoading}
                                                                />
                                                                <FormField
                                                                    required
                                                                    type="monthyear"
                                                                    name={`previousVisa.${index}.end`}
                                                                    label="endDate"
                                                                    disabled={isLoading}
                                                                />
                                                            </FormGridLayout>
                                                        </Paper>
                                                    ))
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {translate('noPastOrFutureVisas')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        )}
                                    </FieldArray>
                                </FormGridLayout>

                                <StepNavigation
                                    isLastStep={isLastStep}
                                    onBack={onBack}
                                    isSubmitting={isLoading}
                                    disableNext={!isValid}
                                />
                            </Form>
                        </FormBusyProvider>
                    );
                }}
            </Formik>
            {unlinkSpouseOpen && (
                <ConfirmationDialog
                    title={translate('unlinkSpouseTitle')}
                    content={translate('unlinkSpouseMessage')}
                    onClose={handleUnlinkSpouseCancel}
                    onConfirm={handleUnlinkSpouseConfirm}
                />
            )}
        </Box>
    );
};

export default AdditionalDetailsStep;
